import { Fragment, useEffect, useState } from 'react';
import PageHeader from '../../layouts/layoutcomponents/PageHeader';
import { useFetch } from '@np/np_webcomponentlib';
import { ChartBy } from './ChartBy';
import { API_OPERATIONS_VIEW } from '../../shared/consts/api';
import { formatAmount } from '../../shared/utils/formatAmount';

type Period = '14d' | '30d' | '3m';

const SpinnerGeneral = () => (
  <div className="dimmer active">
    <div className="spinner1">
      <div className="double-bounce1"></div>
      <div className="double-bounce2"></div>
    </div>
  </div>
);

const periodToDates = (p: Period): { from: Date; to: Date } => {
  const to = new Date();
  const from = new Date();
  if (p === '14d') from.setDate(to.getDate() - 14);
  else if (p === '30d') from.setDate(to.getDate() - 30);
  else from.setMonth(to.getMonth() - 3);
  return { from, to };
};

const periodLabel = (p: Period) =>
  p === '14d' ? 'за 14 дней' : p === '30d' ? 'за 30 дней' : 'за 3 месяца';

const DashboardBusiessMain = ({ idBusinessProps }: { idBusinessProps: string | null }) => {
  const [, doFetchLoad] = useFetch(`${API_OPERATIONS_VIEW}dashboard/mainwithcurrency/platform`);
  const [entity, setEntity] = useState<any>();
  const [idBusiness, setIDBusiness] = useState<string | null>(idBusinessProps);
  const [period, setPeriod] = useState<Period>('14d');

  useEffect(() => { setIDBusiness(idBusinessProps); }, [idBusinessProps]);

  const fetchData = () => {
    const { from, to } = periodToDates(period);
    doFetchLoad({
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      data: { IDBusiness: idBusiness, ForDate: from, ForDateEnd: to },
    }).then(r => setEntity(r.data));
  };

  useEffect(() => { fetchData(); }, [idBusiness, period]); // eslint-disable-line

  const ab = entity?.amountBalance;
  const ar = entity?.amountInReserve;
  const ai = entity?.amountPayIn;
  const ao = entity?.amountPayOut;

  const payInByDay = entity?.dataChartList?.find((c: any) => c.name === 'payInByDay');
  const payOutByDay = entity?.dataChartList?.find((c: any) => c.name === 'payOutByDay');
  const payInCategories: string[] = payInByDay?.xData ?? [];
  const payInSeries: number[] = payInByDay?.yDataAmount ?? [];
  const payOutCategories: string[] = payOutByDay?.xData ?? [];
  const payOutSeries: number[] = payOutByDay?.yDataAmount ?? [];

  return (
    <Fragment>
      <PageHeader titles="Dashboard" active="Dashboard" items={['Home']} />

      <div className="trt-dash">
        {/* HERO: PAY IN + PAY OUT */}
        <div className="trt-row trt-row-2">
          <div className="trt-card trt-card-hero">
            <span className="trt-card-label">PAY IN</span>
            <div className="trt-card-value">
              <span>{formatAmount(ai?.amountInCurrencyPayment)}</span>
              <span style={{ fontSize: 32, fontWeight: 600, color: 'var(--trt-text-muted)' }}>
                {ai?.currencyPayment ?? ''}
              </span>
            </div>
            <div className="trt-card-sub">
              {formatAmount(entity?.countPayIn)} операций · {periodLabel(period)}
            </div>
            <div className="trt-card-sub" style={{ marginTop: 10, fontSize: 13 }}>
              в балансе: <strong style={{ color: 'var(--trt-text)' }}>
                {formatAmount(ai?.amountInCurrencyBalance)} {ai?.currencyBalance ?? ''}
              </strong>
            </div>
          </div>
          <div className="trt-card trt-card-hero">
            <span className="trt-card-label">PAY OUT</span>
            <div className="trt-card-value">
              <span>{formatAmount(ao?.amountInCurrencyPayment)}</span>
              <span style={{ fontSize: 32, fontWeight: 600, color: 'var(--trt-text-muted)' }}>
                {ao?.currencyPayment ?? ''}
              </span>
            </div>
            <div className="trt-card-sub">
              {formatAmount(entity?.countPayOut)} операций · {periodLabel(period)}
            </div>
            <div className="trt-card-sub" style={{ marginTop: 10, fontSize: 13 }}>
              в балансе: <strong style={{ color: 'var(--trt-text)' }}>
                {formatAmount(ao?.amountInCurrencyBalance)} {ao?.currencyBalance ?? ''}
              </strong>
            </div>
          </div>
        </div>

        {/* CHART CARD: PAY IN by day */}
        <div className="trt-card trt-chart-card">
          <div className="trt-chart-head">
            <div className="trt-chart-title">
              <span>📈</span>
              <span>PAY IN — объём по дням</span>
            </div>
            <div className="trt-chart-toggle">
              <button className={period === '14d' ? 'active' : ''} onClick={() => setPeriod('14d')}>14 дней</button>
              <button className={period === '30d' ? 'active' : ''} onClick={() => setPeriod('30d')}>30 дней</button>
              <button className={period === '3m' ? 'active' : ''} onClick={() => setPeriod('3m')}>3 мес</button>
            </div>
          </div>
          {entity ? (
            <ChartBy data={payInSeries} categories={payInCategories} />
          ) : (
            <SpinnerGeneral />
          )}
        </div>

        {/* CHART CARD: PAY OUT by day */}
        <div className="trt-card trt-chart-card">
          <div className="trt-chart-head">
            <div className="trt-chart-title">
              <span>📉</span>
              <span>PAY OUT — объём по дням</span>
            </div>
          </div>
          {entity ? (
            <ChartBy data={payOutSeries} categories={payOutCategories} />
          ) : (
            <SpinnerGeneral />
          )}
        </div>

        {/* SECONDARY KPI */}
        <div className="trt-row trt-row-3">
          <div className="trt-card">
            <div className="trt-card-sub" style={{ marginBottom: 6 }}>Общий баланс</div>
            <div className="trt-card-value-small">
              {formatAmount(ab?.amountInCurrencyBalance)}{' '}
              <span style={{ fontSize: 18, color: 'var(--trt-text-muted)' }}>{ab?.currencyBalance ?? ''}</span>
            </div>
            <div className="trt-card-sub" style={{ fontSize: 12 }}>
              в платёжной валюте: {formatAmount(ab?.amountInCurrencyPayment)} {ab?.currencyPayment ?? ''}
            </div>
          </div>
          <div className="trt-card">
            <div className="trt-card-sub" style={{ marginBottom: 6 }}>В процессе выплаты</div>
            <div className="trt-card-value-small">
              {formatAmount(ar?.amountInCurrencyBalance)}{' '}
              <span style={{ fontSize: 18, color: 'var(--trt-text-muted)' }}>{ar?.currencyBalance ?? ''}</span>
            </div>
            <div className="trt-card-sub" style={{ fontSize: 12 }}>
              зарезервировано: {formatAmount(ar?.amountInCurrencyPayment)} {ar?.currencyPayment ?? ''}
            </div>
          </div>
          <div className="trt-card">
            <div className="trt-card-sub" style={{ marginBottom: 6 }}>Запросов всего</div>
            <div className="trt-card-value-small">
              {formatAmount((Number(entity?.countPayIn) || 0) + (Number(entity?.countPayOut) || 0))}
            </div>
            <div className="trt-card-sub" style={{ fontSize: 12 }}>
              Pay In: {formatAmount(entity?.countPayIn)} / Pay Out: {formatAmount(entity?.countPayOut)}
            </div>
          </div>
        </div>
      </div>
    </Fragment>
  );
};

export default DashboardBusiessMain;
